import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  hydrateBestLoanPlannerSnapshot,
  saveLoanPlannerSnapshot,
} from '../core/storage/loanPlannerStorage';
import { useHousehold } from '../contexts/HouseholdContext';
import { applyHomeLoanToHousehold } from '../utils/applyLoanToHousehold';
import type { ExtraPayment, LoanCostSummary, LoanPaidToDate } from '../utils/loanAmortization';
import {
  BLANK_LOAN_PRINCIPAL,
  BLANK_LOAN_RATE_PCT,
  DEFAULT_LOAN_TERM_YEARS,
  HOME_LOAN_PLANNER_ID,
  loanPlannerLookupIds,
  resolveLoanPlannerInputs,
} from '../utils/loanPlannerSeed';
import { useLoanSnapshot } from './useLoanSnapshot';
import {
  buildAmortizationSchedule,
  calculateMonthlyPayment,
  loanCostSummary,
  scheduleToYearlyBalance,
  summarizePaidToDate,
} from '../utils/loanAmortization';

const SAVE_DEBOUNCE_MS = 400;

export type HomeLoanPlanner = {
  hasMortgageSeed: boolean;
  hydrated: boolean;
  label: string;
  principal: number;
  interestRatePct: number;
  termYears: number;
  monthlyPayment: number;
  extras: ExtraPayment[];
  recurringExtra: number;
  startMonth: string | null;
  setPrincipal: (value: number) => void;
  setInterestRatePct: (value: number) => void;
  setTermYears: (value: number) => void;
  setRecurringExtra: (value: number) => void;
  setStartMonth: (value: string | null) => void;
  setExtraForMonth: (month: number, amount: number) => void;
  addExtraPayment: (month: number, amount: number) => void;
  removeExtraPayment: (month: number) => void;
  baseline: ReturnType<typeof buildAmortizationSchedule>;
  accelerated: ReturnType<typeof buildAmortizationSchedule>;
  baselineSeries: { year: number; balance: number }[];
  acceleratedSeries: { year: number; balance: number }[];
  baselineCost: LoanCostSummary;
  acceleratedCost: LoanCostSummary;
  monthsSaved: number;
  interestSaved: number;
  totalCostSaved: number;
  hasAnyExtra: boolean;
  paidToDate: LoanPaidToDate | null;
};

export function useHomeLoanPlanner(): HomeLoanPlanner {
  const { mortgage } = useLoanSnapshot();
  const { setCore } = useHousehold();
  const loanId = HOME_LOAN_PLANNER_ID;

  const [hydrated, setHydrated] = useState(false);
  const [hasSeed, setHasSeed] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [principal, setPrincipalState] = useState(BLANK_LOAN_PRINCIPAL);
  const [interestRatePct, setInterestRatePctState] = useState(BLANK_LOAN_RATE_PCT);
  const [termYears, setTermYearsState] = useState(DEFAULT_LOAN_TERM_YEARS);
  const [label, setLabel] = useState('Home loan');
  const [extras, setExtras] = useState<ExtraPayment[]>([]);
  const [recurringExtra, setRecurringExtraState] = useState(0);
  const [startMonth, setStartMonthState] = useState<string | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setPrincipal = useCallback((value: number) => {
    setDirty(true);
    setPrincipalState(value);
  }, []);
  const setInterestRatePct = useCallback((value: number) => {
    setDirty(true);
    setInterestRatePctState(value);
  }, []);
  const setTermYears = useCallback((value: number) => {
    setDirty(true);
    setTermYearsState(value);
  }, []);
  const setRecurringExtra = useCallback((value: number) => {
    setDirty(true);
    setRecurringExtraState(value);
  }, []);
  const setStartMonth = useCallback((value: string | null) => {
    setDirty(true);
    setStartMonthState(value);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setHydrated(false);
    setDirty(false);

    hydrateBestLoanPlannerSnapshot(
      loanPlannerLookupIds(mortgage?.id),
      HOME_LOAN_PLANNER_ID,
    ).then((snapshot) => {
      if (cancelled) return;
      const next = resolveLoanPlannerInputs(loanId, mortgage, snapshot);
      setPrincipalState(next.principal);
      setInterestRatePctState(next.interestRatePct);
      setTermYearsState(next.termYears);
      setLabel(next.label);
      setExtras(next.extras);
      setRecurringExtraState(next.recurringExtra);
      setStartMonthState(next.startMonth);
      setHasSeed(next.hasSeed);
      setHydrated(true);
    });

    return () => {
      cancelled = true;
    };
    // Re-hydrate when mortgage seed identity changes; snapshot wins over seed.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mortgage id gate
  }, [mortgage?.id]);

  useEffect(() => {
    // Only persist after the user edits. Hydrating a partner's shared loan
    // must not copy it onto this account (that would duplicate the mortgage).
    if (!hydrated || !dirty) return;
    if (!(principal > 0)) return;

    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      void saveLoanPlannerSnapshot({
        loanId,
        principal,
        interestRatePct,
        termYears,
        extras,
        recurringExtra,
        startMonth,
        updatedAt: new Date().toISOString(),
      });

      void setCore((prev) =>
        applyHomeLoanToHousehold(prev, {
          loanId: HOME_LOAN_PLANNER_ID,
          principal,
          interestRatePct,
          termYears,
          label,
        }),
      );
    }, SAVE_DEBOUNCE_MS);

    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [
    hydrated,
    dirty,
    loanId,
    principal,
    interestRatePct,
    termYears,
    extras,
    recurringExtra,
    startMonth,
    label,
    setCore,
  ]);

  const termMonths = Math.max(1, Math.round(termYears * 12));
  const monthlyPayment = useMemo(
    () => calculateMonthlyPayment(principal, interestRatePct, termMonths),
    [principal, interestRatePct, termMonths],
  );

  const baseline = useMemo(
    () =>
      buildAmortizationSchedule({
        principal,
        interestRatePct,
        termMonths,
        monthlyPayment,
      }),
    [principal, interestRatePct, termMonths, monthlyPayment],
  );

  const accelerated = useMemo(
    () =>
      buildAmortizationSchedule({
        principal,
        interestRatePct,
        termMonths,
        monthlyPayment,
        extras,
        recurringMonthlyExtra: recurringExtra,
      }),
    [principal, interestRatePct, termMonths, monthlyPayment, extras, recurringExtra],
  );

  const baselineSeries = useMemo(
    () => scheduleToYearlyBalance(baseline.rows),
    [baseline.rows],
  );
  const acceleratedSeries = useMemo(
    () => scheduleToYearlyBalance(accelerated.rows),
    [accelerated.rows],
  );

  const baselineCost = useMemo(
    () => loanCostSummary(principal, baseline.totalInterest),
    [principal, baseline.totalInterest],
  );
  const acceleratedCost = useMemo(
    () => loanCostSummary(principal, accelerated.totalInterest),
    [principal, accelerated.totalInterest],
  );

  const monthsSaved = Math.max(0, baseline.monthsToPayoff - accelerated.monthsToPayoff);
  const interestSaved = Math.max(0, baseline.totalInterest - accelerated.totalInterest);
  const totalCostSaved = Math.max(0, baselineCost.totalCost - acceleratedCost.totalCost);
  const hasAnyExtra = recurringExtra > 0 || extras.length > 0;
  const paidToDate = useMemo(
    () =>
      summarizePaidToDate({
        rows: accelerated.rows,
        principal,
        startMonth,
      }),
    [accelerated.rows, principal, startMonth],
  );

  const setExtraForMonth = useCallback((month: number, amount: number) => {
    setDirty(true);
    const safeMonth = Math.max(1, Math.round(month));
    const safeAmount = Math.max(0, Math.round(amount * 100) / 100);
    setExtras((prev) => {
      const without = prev.filter((e) => e.month !== safeMonth);
      if (safeAmount <= 0) return without;
      return [...without, { month: safeMonth, amount: safeAmount }].sort(
        (a, b) => a.month - b.month,
      );
    });
  }, []);

  const addExtraPayment = useCallback((month: number, amount: number) => {
    setDirty(true);
    const safeMonth = Math.max(1, Math.round(month));
    const safeAmount = Math.max(0, Math.round(amount * 100) / 100);
    if (safeAmount <= 0) return;
    setExtras((prev) => {
      const existing = prev.find((e) => e.month === safeMonth);
      if (existing) {
        return prev
          .map((e) =>
            e.month === safeMonth
              ? { month: safeMonth, amount: Math.round((e.amount + safeAmount) * 100) / 100 }
              : e,
          )
          .sort((a, b) => a.month - b.month);
      }
      return [...prev, { month: safeMonth, amount: safeAmount }].sort(
        (a, b) => a.month - b.month,
      );
    });
  }, []);

  const removeExtraPayment = useCallback((month: number) => {
    setDirty(true);
    setExtras((prev) => prev.filter((e) => e.month !== month));
  }, []);

  return {
    hasMortgageSeed: hasSeed || !!mortgage,
    hydrated,
    label,
    principal,
    interestRatePct,
    termYears,
    monthlyPayment,
    extras,
    recurringExtra,
    startMonth,
    setPrincipal,
    setInterestRatePct,
    setTermYears,
    setRecurringExtra,
    setStartMonth,
    setExtraForMonth,
    addExtraPayment,
    removeExtraPayment,
    baseline,
    accelerated,
    baselineSeries,
    acceleratedSeries,
    baselineCost,
    acceleratedCost,
    monthsSaved,
    interestSaved,
    totalCostSaved,
    hasAnyExtra,
    paidToDate,
  };
}
